// Phase 29 (items 1/2/3, Stage-3 C1–C27) — the compact column: a narrow
// stack of per-section collapsible panels (C46 order: communication / timer
// / tasks due today / today's events / general chat). ONE component for both
// hosts:
//   • desktop: portaled live from the main React tree into the pop-out
//     document (Document PiP or window.open fallback);
//   • phone: rendered in-app as the launch surface (phones never open a
//     window).
//
// Stage-3 C25/C26 — the task and event rows ARE the Phase-26 shared row
// components (CaseTaskRow / CaseEventRow) in their `reduced` variant: same
// presentation as CV/CCV minus assignees / priority / due date, with the
// interaction surface cut to (a) the timer triangle, (b) the status pill
// (the C45 cycle toggle — same flow as CV/CCV, see TkStatusTag in
// App.jsx), and (c) subtasks (visible + tickable via the
// shared checklist). Title/description clicks NAVIGATE the main window to
// the item in context (Dashboard row-click semantics: queueScrollTo* +
// nav); caseless items are not clickable, exactly like the Dashboard.
//
// Stage-3 C14 — collapse is CONTENT-DRIVEN, not persisted: a section is
// open by default exactly when it has live content. Arrival and emptying
// transitions reset any manual toggle; manual toggles win between
// transitions.
//
// Adding a future section (Phase 33: missed calls data) = wiring one more
// descriptor in the `sections` array below — the chrome renders any
// descriptor; no other plumbing is needed.
//
// 209 §27 [Laurent; addendum 6] — ONE VIEW AT A TIME. The header's four icons —
// « My », the HOURGLASS, the bell, the phone — each show their own view alone,
// and each toggles back to the hourglass view, which the column opens on: the
// timed rows (the row every section shows, led by the timer's elapsed, a timed
// subtask under its row) with the focused timer's controls, « Tâches et
// événements du jour » and « Tâches en retard » — one row everywhere (the
// reduced CaseTaskRow / CaseEventRow, extended: the title above the case's name
// and number, the note icon, the status pill back at the end). A timed task
// shows only at the top. The bell is the main one (NotifGroupedList); the phone
// view fills its height. Folded: the title bar and the timers.

import { Fragment, useState, useEffect, useLayoutEffect, useRef } from "react";
import {
  MessagesSquare,
  Bell,
  Grid2x2,
  SquareArrowOutUpRight,
  ChevronDown,
  ChevronRight,
  FoldVertical,
  UnfoldVertical,
  Phone,
  PhoneIncoming,
  PhoneOff,
  PhoneOutgoing,
  Hourglass, // 209 §27 — the timer view's own icon
} from "lucide-react";
import { useTranslation } from "../i18n";
import { usePermissions } from "../hooks/usePermissions"; // 190 §E.1 — the compact matrix's own key
import { fmtElapsed, queueScrollToTk, queueScrollToEv, isNotifRead } from "../utils";
import { SidebarGenChat } from "./ChatView";
import CaseTaskRow from "../components/CaseTaskRow";
import CaseEventRow from "../components/CaseEventRow";
import CallsPanel from "../components/CallsPanel";
import { armCallPush } from "../lib/webPush";
import { TaskMatrix } from "../TaskMatrix";
import SelfTick from "../components/SelfTick";
import { selectDueToday, splitDueToday, datePart } from "../lib/dueToday"; // 209 §27 — the two lists
import NotifGroupedList from "../components/NotifGroupedList"; // 209 §27 (d) — the pop-out's bell is the main one
import { ScrollHint, useScrollHint, OPEN_MODE_GLOW, OPEN_MODE_ICON } from "../components/SharedUI"; // 151E §1(c); [AA] the lock's glow
import { timerSubject, timerKey, type TimerRow } from "../lib/timerSubject"; // brief 91 §3a; 209 §27 — the row's type
import { FS, RAD } from "../lib/tokens";
import Table from "../components/Table";

const todayISO = () => new Date().toISOString().slice(0, 10);
const noop = () => {};

/* 209 §27 — the shape the timer view reads of a task or an event, named (the
   column's props are untyped; the `any` ratchet counts what is written). A timer
   row is lib/timerSubject's own `TimerRow`. */
interface ColRow {
  id?: unknown;
  caseId?: unknown;
  ccId?: unknown;
  caId?: unknown;
  [k: string]: unknown;
}
const POM_R = 21;
const POM_CIRC = 2 * Math.PI * POM_R;

export default function CompactColumn({
  inPopOut,
  onToggle,
  // timer
  activeTimers,
  focusedTimerIdx,
  startTimer,
  pauseTimer,
  stopTimerAsk,
  stopAllTimers,
  pomInterval,
  getPomElapsed,
  // data
  tk,
  sTk,
  ag,
  sAg,
  ca,
  cc,
  co,
  OPEN_ST,
  currentUser,
  // actions (main window)
  om,
  nav,
  /* Round 14 (C85) — guarded case navigation for every tap-through below
     (task/event titles, the Eisenhower jump, timer-row descriptions, matrix
     task clicks): App's tryNavCase checks the viewer's actual case/cc
     visibility and toasts instead of navigating to a blank CV. */
  tryNavCase,
  svTkData,
  // C61 — subtask done-tick → the main-window complete-style prompt
  // (time-log offer); wrapped in inMain below so the pop-out pulls focus.
  onSubtaskDone,
  // shared-row wiring (C25) — same batch CV/CCV pass to the Phase-26 rows
  tkTypes,
  evtTypes,
  itemLevelList,
  priMap,
  TkStatusTag,
  etTag,
  evtLongestCode, // 139 §1 — travels with etTag; `reduced` ignores it
  locIcon,
  coAvs,
  MultiSelect,
  s,
  SK,
  permIsAdmin,
  // C31/C33 — Eisenhower matrices (case = caseEisenhower via svTkData; My =
  // per-user userMatrix) + header bell/notifications + pop-out resize.
  userMatrix,
  userMatrixQuadrant,
  userMatrixSetQuadrant,
  notifications = [],
  unreadNotifCount = 0,
  /* 209 §26 (addendum 6) — the badge counts what is new since a bell was last
     opened (App's `bellNew`), and an open bell here records what it shows
     (`onBellShown`, App's `markBellSeen`) — the header's bell and this one keep
     ONE record, so they agree. `unreadNotifCount` still gates « Tout marquer
     comme lu ». */
  bellNewCount = 0,
  onBellShown,
  /* 209 §27 (d) — THE POP-OUT'S BELL IS THE MAIN ONE: `NotifGroupedList` over the
     same rows, so a row needs here what it needs there (App's bell mount). */
  evtEntries = [],
  dismissNotif,
  joinAndTime,
  notifRead = [],
  markNotifRead,
  markAllRead,
  // general chat passthrough (state shared with the sidebar instance)
  gChat,
  setGChat,
  gChatInput,
  setGChatInput,
  gChatLinkCase,
  setGChatLinkCase,
  // 132 §1 — the link's kind travels with its id; the query state went to
  // MatterPicker with the control that owned it. `getRecentCases` arrives for
  // the picker's recents arm — `cc` was already here (:81).
  gChatLinkKind,
  setGChatLinkKind,
  getRecentCases,
  chatMsgs,
  setChatMsgs,
  chatRead,
  setChatRead,
  gChatUnread,
  firmName,
  /* Phase 31 (C2) — phone surface (header icon + RED missed badge) and the
     incoming ring banner; accept/decline run via the App-level handlers.
     Corr-2 (item 4) — the icon opens the calls panel INLINE (the bell /
     matrix colOv pattern, both hosts), so the pop-out keeps its autonomy:
     presence/log data + the call action come down as props. */
  canCalls = false,
  missedCallCount = 0,
  incomingCalls = [],
  onCallAccept,
  onCallDecline,
  presenceIds = [],
  presenceLoaded = false,
  calls = [],
  onCall,
  /* Corr — case-free internal communication: offline→missed + the three
     Section-2 modals; callActive collapses the direct-calls pane. */
  onNotifyMissed,
  callActive = false,
  onScheduleMeeting,
  onSchedulePoll,
  onSendEmail,
  callBusy = false,
  onPullPresence,
  /* 31.1 (item 9) — pop-out-hosted waiting banner: a call initiated FROM
     this column (host 'popout') waits HERE, in the banner idiom — no
     main-window bounce. Lifecycle handlers come down from App; the banner
     derives its state from the live `calls` row like the main card. */
  outgoingCall = null,
  onAbortCall,
  onCloseOutgoing,
  /* Phase 31 (C3) — ephemeral engine alerts mirrored into the pop-out. */
  alertToasts = [],
  onDismissAlert,
}: any) {
  const { t } = useTranslation();
  /* 190 §E.1 — `surface.eisenhower.compact`: hidden firm-wide or withheld
     from a role, the "My" matrix button and its panel are gone together. The
     pop-out renders in the main window's React tree, so the permission store
     is the one every other surface reads. */
  const { can: surfaceCan } = usePermissions();
  const canCompactMatrix = surfaceCan("surface.eisenhower.compact");
  const today = todayISO();

  // Act in the MAIN window and pull it forward (pop-out actions must open
  // modals/dialogs in the full app, never locally).
  // C49 audit (round 9) — this focus pull is reserved for actions that
  // GENUINELY open something in the main window: tap-through navigation
  // (task/event title + timer-row description clicks, notification items,
  // chat case-chips, matrix task clicks, the C62 row Eisenhower jump), the
  // stop / stop-all → time-log modal, the subtask "Log time" modal, and the
  // C61 subtask-done prompt (it renders in the main window). Everything
  // else in the column (status cycling between open statuses, section
  // collapse, fold/unfold, timer pause/play/switch, chat send, the
  // in-column matrix and bell panels, un-ticking a subtask, Eisenhower
  // moves) is focus-neutral and must stay that way — do not add inMain to
  // those paths. Neither Document PiP nor the window.open fallback ever
  // focuses the opener on its own; every pull is an explicit
  // window.focus() here or in TkStatusTag (completed dialog).
  const inMain = (fn: any) => {
    try {
      window.focus();
    } catch {}
    fn();
  };
  const omMain = (...a: any[]) => inMain(() => om(...a));
  /* C26 — title-click navigation: focus the main window; on the PHONE
     (in-app column) also flip back to the full app so the navigation is
     actually visible (the pop-out stays open on desktop). */
  const goNav = (fn: any) => {
    inMain(fn);
    if (!inPopOut && onToggle) onToggle();
  };
  /* C85 — guarded jump: the focus pull runs either way (the refusal toast
     renders in the MAIN window — pulling it forward makes it visible), but
     the phone column flips back to the app and the scroll queues ONLY when
     navigation actually happened. */
  const _navCase = tryNavCase || ((kind: any, id: any) => (nav(kind, id), true));
  const navTask = (t2: any) =>
    inMain(() => {
      const kind = t2.caseId != null ? "ca" : "cc";
      if (!_navCase(kind, t2.caseId != null ? t2.caseId : t2.ccId)) return;
      queueScrollToTk(t2.id);
      if (!inPopOut && onToggle) onToggle();
    });
  const navEvent = (ev: any) =>
    inMain(() => {
      const cid = ev.caId != null ? ev.caId : ev.caseId;
      if (!_navCase(cid != null ? "ca" : "cc", cid != null ? cid : ev.ccId))
        return;
      queueScrollToEv(ev.id);
      if (!inPopOut && onToggle) onToggle();
    });

  /* ── C31/C33 panels, C56 INLINE — the My Eisenhower matrix and the
     notifications list render as chrome-less blocks at the top of the
     column body: no title bar, no X, no backdrop (the CV/CCV
     embedded-matrix pattern). The icon that opened a panel closes it
     (toggle). C62 — only the HEADER "My" control opens the matrix now;
     the per-row Eisenhower badge navigates instead (see onEisenClick at
     the row). Section collapse state is untouched. `colOv` =
       { kind:'matrix', caseId:null } | { kind:'notif' }
     C55 — in the POP-OUT, opening a panel that needs more height than the
     window offers GROWS the window height to fit (width untouched), and
     closing restores the height captured at open — the C50 fold mechanics
     exactly: height-only, inside the toggle click's (or Escape keydown's)
     transient activation via the synchronously-flushed layout effect, ONE
     resize per transition, catch → internal scroll (.CColB overflow) when
     the browser refuses or clamps. Fold/unfold clicks close the panel and
     reset its restore baseline (C50's own restore then owns the height,
     so the two height memories can never fight). */
  const COL_INSET = 8;
  const [colOv, setColOv] = useState<any>(null);
  const ovRootRef = useRef<any>(null);
  const colRootRef = useRef<any>(null);
  const prevOvRef = useRef<any>(null);
  const panelPrevHRef = useRef<any>(null);
  const toggleOv = (ov: any) =>
    setColOv((cur: any) =>
      cur &&
      cur.kind === ov.kind &&
      (cur.caseId ?? null) === (ov.caseId ?? null)
        ? null
        : ov,
    );
  const closeOv = () => setColOv(null);
  useLayoutEffect(() => {
    const prev = prevOvRef.current;
    prevOvRef.current = colOv;
    const root = colRootRef.current;
    if (!inPopOut || !root) return;
    const doc = root.ownerDocument;
    const win = doc.defaultView;
    if (!win || win === window) return;
    try {
      if (colOv) {
        // Open or swap: grow-only, by the column body's overflow amount.
        // scrollHeight/clientHeight are CSS px; the window's innerHeight is
        // VISUAL px (the pop-out renders under the mirrored documentElement
        // zoom), so scale the deficit by the computed zoom.
        const b = doc.querySelector(".CColB");
        if (!b) return;
        const z =
          parseFloat(win.getComputedStyle(doc.documentElement).zoom) || 1;
        const deficit = Math.ceil((b.scrollHeight - b.clientHeight) * z);
        if (deficit > 0) {
          if (panelPrevHRef.current == null)
            panelPrevHRef.current = win.outerHeight;
          win.resizeBy(0, deficit);
        }
      } else if (prev && panelPrevHRef.current != null) {
        win.resizeTo(win.outerWidth, panelPrevHRef.current);
        panelPrevHRef.current = null;
      }
    } catch {
      /* refused/clamped — internal scroll remains the honest fallback. */
    }
  }, [colOv]);
  useEffect(() => {
    if (!colOv) return;
    // Listen on the panel's OWN document — in the pop-out that is the
    // pop-out document, not the main one (the ref is populated by the time
    // this post-commit effect runs).
    const doc = ovRootRef.current?.ownerDocument || document;
    const onKey = (e: any) => {
      if (e.key === "Escape") closeOv();
    };
    doc.addEventListener("keydown", onKey);
    return () => doc.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colOv]);
  /* Corr-2 (item 4) — presence poll while the inline calls panel is open
     (the same 30 s cadence as the main-window panel; App owns the state). */
  const callsOvOpen = !!colOv && colOv.kind === "calls";
  /* 209 §26 — while this bell shows its list, what it shows is seen. */
  const bellOvOpen = (colOv as { kind?: string } | null)?.kind === "notif";
  const bellShown = onBellShown as (() => void) | undefined;
  useEffect(() => {
    if (bellOvOpen && typeof bellShown === "function") bellShown();
  });
  useEffect(() => {
    if (!callsOvOpen || !onPullPresence) return;
    onPullPresence();
    const iv = setInterval(onPullPresence, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callsOvOpen]);
  /* C62 — the per-row Eisenhower badge no longer toggles an inline case
     matrix (it rendered over its own toggle — dead end); the row wires
     navTask straight onto onEisenClick below, same gate and semantics as
     the title click. Only the header "My" matrix (S6) opens inline. */

  /* C65 — third arg: the task row itself. An off-team delegatee has no
     matching case/cc row in his lists, but his assigned rows carry the
     server-attached caseTitle/ccTitle (+refs) — display-only fallback. */
  /* 209 §27 — the case's NAME AND NUMBER, under the task (Laurent: « case name
     and number on bottom (now is reverse) »): the timer rows showed the case
     number alone above the task, the task rows the name alone before it. Both,
     name first, from the viewer's lists, else the server-attached hints. */
  const caseNameNumber = (caseId: unknown, ccId: unknown, row: ColRow | null | undefined): string => {
    const both = (name: unknown, num: unknown) =>
      [name, num].filter((x) => typeof x === "string" && x).join(" · ");
    if (caseId != null) {
      const c = (ca || []).find((x: ColRow) => x.id === caseId);
      return c ? both(c.title, c.ref) : both(row?.caseTitle, row?.caseRef);
    }
    if (ccId != null) {
      const c = (cc || []).find((x: ColRow) => x.id === ccId);
      return c ? both(c.title, c.ref) : both(row?.ccTitle, row?.ccRef);
    }
    return "";
  };

  /* ── (i) Timer ─────────────────────────────────────────────────────── */
  const timerCount = (activeTimers || []).length;
  const focused =
    timerCount > 0 ? activeTimers[focusedTimerIdx] || activeTimers[0] : null;
  /* 209 §27 — THE TIMED ROWS ARE THE ROW EVERY SECTION SHOWS (the reduced
     CaseTaskRow / CaseEventRow, extended), led by the timer's elapsed: a click on
     it switches the timer to this row (the tag did it in the column's own row,
     which is gone). A timed task shows ONLY here — it leaves « Tâches et
     événements du jour » and « Tâches en retard » while its timer exists. */
  const elapsedLead = (tm: TimerRow, i: number) => (
    <button
      type="button"
      className="colTkEl"
      title={t("app.timer.focus_switch", { fallback: "Focus / resume" })}
      style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "inherit" }}
      onClick={() =>
        startTimer(tm.taskId, tm.subtaskId || undefined, {
          eventId: tm.eventId || null,
        })
      }
    >
      <SelfTick active={!tm.paused}>
        {() => (
          <span style={{ color: tm.paused ? "var(--tm)" : "var(--gn)", fontWeight: i === focusedTimerIdx ? 700 : 500 }}>
            {fmtElapsed(tm.paused ? (tm.elapsed ?? 0) : (tm.elapsed ?? 0) + (Date.now() - (tm.startedAt ?? Date.now())))}
          </span>
        )}
      </SelfTick>
    </button>
  );
  const timedTaskIds = new Set<unknown>();
  const timedEventIds = new Set<unknown>();
  const timedTasks: { tm: TimerRow; i: number; task: ColRow; st: ColRow | null }[] = [];
  const timedEvents: { tm: TimerRow; i: number; ev: ColRow; st: ColRow | null }[] = [];
  (activeTimers || []).forEach((tm: TimerRow, i: number) => {
    const sub = timerSubject(tm, tk, ag) as { kind?: string; parent?: ColRow; subtask?: ColRow } | null;
    if (!sub || !sub.parent) return;
    if (sub.kind === "event") {
      timedEventIds.add(sub.parent.id);
      timedEvents.push({ tm, i, ev: sub.parent, st: sub.subtask || null });
    } else {
      timedTaskIds.add(sub.parent.id);
      timedTasks.push({ tm, i, task: sub.parent, st: sub.subtask || null });
    }
  });
  /* « and under it the subtask which is actually timed, if that's the case, OPEN » */
  const timedSubLine = (st: ColRow | null) =>
    st ? (
      <span style={{ display: "block", fontSize: FS.s11, color: "var(--tp)", padding: "0 8px 6px 30px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={String(st.text ?? "")}>
        {"↳ "}
        {String(st.text ?? "")}
      </span>
    ) : null;
  /* ── (ii) Today's tasks and events, (iii) late tasks — MINE only (C1, C60) ─ */
  // C60 — ownership + viewer-relevant dates live in the selector: task rows
  // for tasks ASSIGNED to me (parent date), my subtasks at their own date.
  // `?? -1` keeps the scope strict for a session with no bound collaborator.
  // 209 §27 — the selector's buckets split in two (lib/dueToday.ts,
  // `splitDueToday`): due today (open, then done today) and late; a timed task
  // shows only at the top.
  const due = selectDueToday(tk, OPEN_ST, today, currentUser ?? -1);
  const split = splitDueToday(due, today);
  const todayRows = split.today.filter((x) => !timedTaskIds.has(x.id));
  const lateRows = split.late.filter((x) => !timedTaskIds.has(x.id));
  const openCount = (rows: readonly ColRow[]) => rows.filter((x) => OPEN_ST.includes(x.status)).length;

  /* 151E §1(c) — the column body already scrolls (.CColB overflow-y auto);
     what it lacked was the AFFORDANCE: on a tall day the content below the
     fold was invisible and unannounced — "the bottom simply falls off". The
     151D §2(d) discs ride the body as its first/last children. */
  const colBHint = useScrollHint();
  const [tkExpanded, setTkExpanded] = useState(() => new Set());
  const [evExpanded, setEvExpanded] = useState(() => new Set());
  const toggleEvExpanded = (id: any) =>
    setEvExpanded((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  /* 209 §27 — ONE ROW for every task of the view (timed, today's, late): the
     reduced CaseTaskRow, extended (its header comment). */
  const taskRow = (t2: ColRow, o: { lead?: unknown; highlighted?: boolean } = {}) => (
    <CaseTaskRow
      key={String(t2.id)}
      reduced
      lead={o.lead}
      highlighted={!!o.highlighted}
      caseLabel={caseNameNumber(t2.caseId, t2.ccId, t2)}
      t={t2}
      co={co}
      currentUser={currentUser}
      tk={tk}
      tkTypes={tkTypes}
      itemLevelList={itemLevelList}
      priMap={priMap}
      activeTimers={activeTimers}
      tkLocked={true}
      canEditOthersTasks={false}
      permIsAdmin={permIsAdmin}
      isRP={false}
      tkExpanded={tkExpanded}
      setTkExpanded={setTkExpanded}
      TkStatusTag={TkStatusTag}
      startTimer={startTimer}
      svTkData={svTkData}
      sTk={sTk}
      openModal={omMain}
      onDeleteTask={noop}
      onNavTask={t2.caseId != null || t2.ccId != null ? navTask : undefined}
      /* C62 — the Eisen badge jumps exactly like the title click (same
         caseless gate: no context → inert). */
      onEisenClick={t2.caseId != null || t2.ccId != null ? navTask : undefined}
      /* C61 — done-tick → complete-style prompt in the MAIN window, with the
         focus pull. */
      onSubtaskDone={
        typeof onSubtaskDone === "function"
          ? (tx: any, st: any) => inMain(() => onSubtaskDone(tx, st))
          : undefined
      }
    />
  );
  /* ── Today's events — involving ME only (C1) ── */
  const evToday = (ag || [])
    .filter((a: any) => {
      if (!a || a.archived || !a.date || datePart(a.date) !== today)
        return false;
      if (timedEventIds.has(a.id)) return false; // 209 §27 — a timed event shows only at the top
      // Involvement: assigned to me, unassigned (= everyone's), or mine by
      // creation — same pattern the notification feed applies.
      const ass = a.assignedCollaborators || a.assigned || [];
      return (
        !ass.length ||
        ass.includes(currentUser) ||
        a.createdById === currentUser
      );
    })
    .sort((a: any, b: any) => ((a.time || "") < (b.time || "") ? -1 : 1));
  /* ONE ROW for every event of the view: the reduced CaseEventRow, extended. */
  const eventRow = (ev: ColRow, lead?: unknown) => (
    <CaseEventRow
      key={String(ev.id)}
      reduced
      lead={lead}
      caseLabel={caseNameNumber(ev.caId ?? ev.caseId ?? null, ev.ccId ?? null, ev)}
      ev={ev}
      co={co}
      currentUser={currentUser}
      todayStr={today}
      priMap={priMap}
      itemLevelList={itemLevelList}
      evtTypes={evtTypes}
      etTag={etTag}
      evtLongestCode={evtLongestCode}
      coAvs={coAvs}
      locIcon={locIcon}
      MultiSelect={MultiSelect}
      ag={ag}
      sAg={sAg}
      s={s}
      SK={SK}
      evExpanded={evExpanded}
      toggleEvExpanded={toggleEvExpanded}
      agLocked={true}
      canEditAgenda={false}
      canEditOthersTasks={false}
      /* brief 91 §3a — the event's timer column, on the same `omMain` the task
         rows use (C83iv: the te modal opens in the MAIN window). */
      activeTimers={activeTimers}
      startTimer={startTimer}
      openModal={omMain}
      onDeleteEvent={noop}
      onNavEvent={ev.caId != null || ev.caseId != null || ev.ccId != null ? navEvent : undefined}
    />
  );
  const todayContent = (
    /* C36 — horizontal inset comes from .colSecB (uniform 8px). */
    <div>
      {todayRows.length === 0 && evToday.length === 0 && (
        <div className="colEmpty">
          {t("col.empty.tasks", { fallback: "Nothing due today" })}
        </div>
      )}
      {todayRows.length > 0 && (
        <Table className="colTbl">
          <tbody>{todayRows.map((x) => taskRow(x))}</tbody>
        </Table>
      )}
      {evToday.map((ev: ColRow) => eventRow(ev))}
    </div>
  );
  const lateContent = (
    <div>
      {lateRows.length > 0 && (
        <Table className="colTbl">
          <tbody>{lateRows.map((x) => taskRow(x))}</tbody>
        </Table>
      )}
    </div>
  );

  /* 209 §27 — the timer section: the timed rows (the view's one row), then the
     focused timer's controls. Defined after the rows it renders. */
  const timerContent = (
    <div className="colTimer">
      {timedTasks.length > 0 && (
        <Table className="colTbl">
          <tbody>
            {timedTasks.map(({ tm, i, task, st }) => (
              <Fragment key={timerKey(tm)}>
                {taskRow(task, { lead: elapsedLead(tm, i), highlighted: i === focusedTimerIdx })}
                {st && (
                  <tr>
                    <td colSpan={6} className="colSubCell">{timedSubLine(st)}</td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </Table>
      )}
      {timedEvents.map(({ tm, i, ev, st }) => (
        <Fragment key={timerKey(tm)}>
          {eventRow(ev, elapsedLead(tm, i))}
          {timedSubLine(st)}
        </Fragment>
      ))}
      {focused && (
        <div className="colTimerCtl">
          <SelfTick active={!focused.paused}>
            {() => {
              const el = focused.paused
                ? focused.elapsed
                : focused.elapsed + (Date.now() - focused.startedAt);
              /* 147 §1.6 — `(pomInterval || 20)` STOOD HERE and was the second
                 of the three sites that disagreed about whether 0 is a value:
                 this ring counted down from twenty while the loop in App.tsx
                 was never going to fire. Zero now means off, and an off
                 pomodoro shows NO RING rather than a frozen one — a ring for a
                 disabled countdown is a lie. The elapsed readout beside it is
                 the timer's, not the pomodoro's, and stays. */
              const intMs = pomInterval * 60 * 1000;
              const pomOn = intMs > 0;
              const pe = getPomElapsed ? getPomElapsed() : 0;
              const prog = pomOn ? (pe % intMs) / intMs : 0;
              const pomMins = pomOn ? Math.ceil((intMs - (pe % intMs)) / 60000) : 0;
              const col = focused.paused ? "var(--am)" : "var(--gn)";
              return (
                <>
                  {pomOn && (
                  <div className="colPom">
                    <svg viewBox="0 0 50 50">
                      <circle
                        cx="25"
                        cy="25"
                        r={POM_R}
                        fill="none"
                        stroke="var(--br)"
                        strokeWidth="3"
                      />
                      <circle
                        cx="25"
                        cy="25"
                        r={POM_R}
                        fill="none"
                        stroke={col}
                        strokeWidth="3"
                        strokeDasharray={POM_CIRC}
                        strokeDashoffset={POM_CIRC * (1 - prog)}
                        strokeLinecap="round"
                        transform="rotate(-90 25 25)"
                      />
                    </svg>
                    <div className="pom-label">{pomMins}m</div>
                  </div>
                  )}
                  <div
                    className={"colTime" + (focused.paused ? "" : " pulse")}
                    style={{ color: col }}
                  >
                    {fmtElapsed(el)}
                  </div>
                </>
              );
            }}
          </SelfTick>
          <div className="colTimerBtns">
            <button
              className={"colBtn " + (focused.paused ? "play" : "pause")}
              /* 116 §3 — RESUME CARRIES THE PARENT KIND BACK, exactly as the
                 focus/switch tag beside it already does (:351, 91 §3a). It did not,
                 and with the engine's key now a TRIPLE that is the difference
                 between resuming this timer and minting a second, empty one:
                 an event timer keys (null, E, null) and this call was sending
                 (null, null, null). The same hole was already live for an event
                 SUBTASK timer under the old pair key — (null, S) matched by
                 luck, because subtask ids are globally unique. */
              onClick={() =>
                focused.paused
                  ? startTimer(focused.taskId, focused.subtaskId || undefined, {
                      eventId: focused.eventId || null,
                    })
                  : pauseTimer()
              }
            >
              {focused.paused
                ? t("app.timer.resume", { fallback: "Resume" })
                : t("app.timer.pause", { fallback: "Pause" })}
            </button>
            <button
              className="colBtn stop"
              onClick={() => inMain(() => stopTimerAsk())}
            >
              {t("app.timer.stop", { fallback: "Stop" })}
            </button>
            {timerCount > 1 && (
              <button
                className="colBtn stop"
                onClick={() => inMain(() => stopAllTimers())}
              >
                {t("app.timer.stop_all", { fallback: "Stop all" })}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );

  /* ── (iv) General chat ─────────────────────────────────────────────── */
  const chatContent = (
    <SidebarGenChat
      {...({
        inCol: true,
        /* 209 §27 — « the phone view fills the height it has »: no four-message
           window; the feed takes the room the panel gives it and scrolls beyond. */
        fill: true,
        gChat,
        setGChat,
        gChatInput,
        setGChatInput,
        gChatLinkCase,
        setGChatLinkCase,
        gChatLinkKind,
        setGChatLinkKind,
        chatMsgs,
        setChatMsgs,
        chatRead,
        setChatRead,
        co,
        ca,
        cc,
        getRecentCases,
        currentUser,
        nav: (...a: any[]) => inMain(() => nav(...a)),
        /* C-1 — linked-case chips run the C85 guard; like navTask/navEvent the
           focus pull runs either way so the refusal toast (main window) is
           visible from the pop-out. */
        tryNavCase: (k: any, i: any) => inMain(() => _navCase(k, i)),
      } as any)}
    />
  );

  /* ── (v) Communication (C35 groundwork) ───────────────────────────── */
  /* Corr-2 (item 5) — the Communication section (C35 inert chips + the C86
     nested chat) is GONE: the header phone icon + inline calls panel carry
     the callable-collaborator surface now. General Chat returns to being
     its own top-level section (the pre-C86 shape), same unread badge. */

  /* ── Section registry ──────────────────────────────────────────────── */
  // C8/C20/C27 — timer, tasks and events headers carry no leading icon;
  // chat and missed-calls keep theirs. C14 — "Missed calls" is visible now,
  // permanently empty until Phase 33 wires a data source.
  // C46 — Communication leads the stack (directly under the title bar,
  // above Timer); then Timer / Tasks / Events / General chat.
  const sections: any[] = [
    /* Corr-2 (item 1) — the standalone "General chat" section is gone: general
       chat now lives as a subsection inside the COMMS (phone) panel, and its
       unread count badges the phone icon. */
    {
      id: "timer",
      icon: null,
      title: t("col.sec.timer", { fallback: "Timer" }),
      badge: timerCount,
      content: timerContent,
    },
    /* 209 §27 — « a "today's tasks and events" section under the timer, same
       presentation … then a "late tasks" section shows all late tasks ». */
    {
      id: "today",
      icon: null,
      title: t("col.sec.today", { fallback: "Today's tasks and events" }),
      badge: openCount(todayRows) + evToday.length,
      content: todayContent,
    },
    {
      id: "late",
      icon: null,
      title: t("dash.kpi.overdue_tasks", { fallback: "Overdue tasks" }),
      badge: lateRows.length,
      content: lateContent,
    },
    /* Round 14 (C86) — the standalone "General Chat" section is gone: the
       chat is the nested sub-section inside Communication above. The comm
       section badge carries the unread count so a fully-collapsed
       Communication section still signals new messages. */
  ];

  /* ── C14 — content-driven collapse ─────────────────────────────────── */
  const hasMap: Record<string, boolean> = {
    timer: timerCount > 0,
    today: openCount(todayRows) + evToday.length > 0, // a done-only list auto-collapses (badge = open only)
    late: lateRows.length > 0,
    gchat: gChatUnread > 0, // corr-2 — chat auto-opens on unread, rests collapsed
  };
  const [secOverride, setSecOverride] = useState<Record<string, any>>({});
  const prevHas = useRef<any>(null);
  useEffect(() => {
    const prev = prevHas.current;
    if (prev) {
      const changed = Object.keys(hasMap).filter(
        (k) => prev[k] !== undefined && prev[k] !== hasMap[k],
      );
      if (changed.length)
        setSecOverride((p) => {
          const n = { ...p };
          changed.forEach((k) => delete n[k]);
          return n;
        });
    }
    prevHas.current = hasMap;
  });
  const isOpen = (id: any) =>
    secOverride[id] !== undefined ? secOverride[id] : !!hasMap[id];
  const toggleSec = (id: any) =>
    setSecOverride((p) => ({ ...p, [id]: !isOpen(id) }));

  /* ── C33 header controls (panels are inline since C56) ─────────────── */
  /* C47 — fold toggle: folds the column down to title bar + Communication
     + Timer; the other sections (Tasks / Events / Chat) are fully unmounted
     while folded, not merely collapsed. Session-local UI state — never
     persisted; the C14 content-driven collapse rules keep governing the
     sections that remain visible. Rendered in both hosts (pop-out + in-app
     phone column) since it lives in the shared title bar. */
  const [folded, setFolded] = useState(false);
  /* C50 — folding also shrinks the POP-OUT WINDOW's height to fit exactly
     title bar + Communication + Timer (width untouched); unfolding restores
     the height captured at fold time. This is the SINGLE permitted
     exception to the C37 no-resize rule, and it runs only inside the
     fold/unfold click's transient user activation: the click handler
     stashes a job, and the useLayoutEffect below — which React flushes
     SYNCHRONOUSLY within the same discrete-event task — measures the
     post-fold DOM and issues exactly one resizeBy/resizeTo. Document PiP
     windows accept resizeBy/resizeTo only with (and consuming) transient
     activation per the WICG spec; Chrome may clamp to its min/max
     overlay sizes, and on refusal the catch leaves the content-fold
     standing on its own. The in-app phone column never resizes anything. */
  const foldJobRef = useRef<any>(null);
  useLayoutEffect(() => {
    const job = foldJobRef.current;
    if (!job) return;
    foldJobRef.current = null;
    try {
      const { win, fold } = job;
      if (fold) {
        // Post-commit DOM = title bar + the kept sections only (the fold
        // click also closed any inline panel). .CColB has no padding/gap
        // and .colSec no margins (App.css), so the needed CSS-px height is
        // the plain sum of offsetHeights.
        const doc = win.document;
        const head = doc.querySelector(".CColH");
        if (!head) return;
        let cssH = head.offsetHeight;
        doc
          .querySelectorAll(".CColB > .colSec")
          .forEach((el: any) => (cssH += el.offsetHeight));
        /* C53 — two corrections vs the round-9 version that shaved the
           timer's Stop control: (1) offsetHeight is CSS px while the
           window's innerHeight is VISUAL px — the pop-out renders under
           the mirrored documentElement zoom (--ui-zoom), so the sum must
           scale by the computed zoom; (2) include the column's bottom
           inset (COL_INSET) so the last row keeps its designed breathing
           room instead of kissing the window edge. Verified against the
           tallest folded state (running timer: pom ring + elapsed +
           Stop/Stop-all cluster). */
        const z =
          parseFloat(win.getComputedStyle(doc.documentElement).zoom) || 1;
        const target = Math.ceil((cssH + COL_INSET) * z);
        win.resizeBy(0, target - win.innerHeight);
      } else {
        /* Round 15 (C93) — UNFOLDED is a FIXED state now: the maximum
           available height, the same constant the open request uses (C83i)
           — not "whatever the height happened to be at fold time" (the old
           captured-height restore made unfold a no-op whenever fold had
           been clipped by a clamp, which read as "the icon changes but the
           window doesn't"). The request is in VISUAL px (screen px), so no
           zoom scaling applies — only the folded MEASUREMENT above needs
           the zoom factor, exactly as in C53. Chrome may clamp the grant
           (C44) — accepted; the REQUEST is constant. */
        const maxH = (win.screen && win.screen.availHeight) || 1080;
        win.resizeTo(win.outerWidth, maxH);
      }
    } catch {
      /* NotAllowedError (activation consumed/expired) or vendor clamp —
         the content fold (C47) stands without the window resize. */
    }
  }, [folded]);
  /* 190 round 6 [AA] — the seventh instance: the pop-out's "My" matrix
     glows while its overlay is open (the lock's declarations, SharedUI
     OPEN_MODE_GLOW), the way out. */
  const matrixOpen = (colOv as { kind?: string } | null)?.kind === "matrix";
  const matrixBtn = canCompactMatrix ? (
    <button
      type="button"
      className="colHdrBtn"
      title={t("header.matrix.title", { fallback: "Priority matrix" })}
      onClick={() => toggleOv({ kind: "matrix", caseId: null })}
      style={matrixOpen ? { ...OPEN_MODE_GLOW, borderRadius: RAD.r6, color: OPEN_MODE_ICON } : undefined}
    >
      <span style={{ fontSize: FS.s11, fontWeight: 600, marginRight: 2 }}>
        {t("header.my", { fallback: "My" })}
      </span>
      <Grid2x2 size={18} />
    </button>
  ) : null;
  /* 209 §27 (a) — « add next to the My Eisenhower a same size Lucide "Hourglass"
     icon, which shows the timer section ». The column opens on this view, and a
     second click on another view's icon returns to it (addendum 6: each icon
     toggles between its own view and the timer view). */
  const hourglassBtn = (
    <button
      type="button"
      className="colHdrBtn"
      title={t("col.sec.timer", { fallback: "Timer" })}
      onClick={() => setColOv(null)}
      style={!colOv ? { ...OPEN_MODE_GLOW, borderRadius: RAD.r6, color: OPEN_MODE_ICON } : undefined}
    >
      <Hourglass size={18} />
    </button>
  );
  const bellBtn = currentUser ? (
    <button
      type="button"
      className="colHdrBtn"
      style={{ position: "relative" }}
      title={t("notif.title", { fallback: "Notifications" })}
      onClick={() => toggleOv({ kind: "notif" })}
    >
      <Bell size={18} />
      {bellNewCount > 0 && <span className="colBellB">{bellNewCount}</span>}
    </button>
  ) : null;
  /* Phase 31 (C2) — phone icon immediately right of the Bell, RED missed
     badge (.colCallB). Corr-2 (item 4) — toggles the INLINE calls panel
     (the bell pattern); it no longer pulls the main window forward. */
  const phoneBtn =
    canCalls && currentUser ? (
      <button
        type="button"
        className="colHdrBtn"
        style={{ position: "relative" }}
        title={t("calls.panel_title", { fallback: "Internal communication" })}
        onClick={() => { armCallPush(); toggleOv({ kind: "calls" }); }}
      >
        <Phone size={18} />
        {/* Corr-2 (item 9) — the phone badge counts missed calls AND general-
            chat unread (general chat now lives in COMMS). Case-chat @mentions
            stay on the Bell. */}
        {missedCallCount + gChatUnread > 0 && (
          <span className="colCallB">{missedCallCount + gChatUnread}</span>
        )}
      </button>
    ) : null;
  const foldBtn = (
    <button
      type="button"
      className="colHdrBtn"
      title={
        folded
          ? t("col.unfold", { fallback: "Unfold" })
          : t("col.fold", { fallback: "Fold" })
      }
      onClick={(e) => {
        const fold = !folded;
        // C50 — pop-out host only: queue the window resize for the
        // synchronous post-commit layout effect (same click task, same
        // transient activation). Round 15 (C93) — no height capture
        // anymore: both states are FIXED requests (fold = measured minimal,
        // unfold = max available), so there is nothing to remember.
        const win =
          inPopOut &&
          e.currentTarget.ownerDocument &&
          e.currentTarget.ownerDocument.defaultView;
        if (win && win !== window) {
          /* C55/C56 interplay — the fold click closes any inline panel and
             CLEARS the panel's height baseline so the panel restore can
             never fight the fold's own resize; this commit issues exactly
             ONE resize (the fold's own). */
          panelPrevHRef.current = null;
          foldJobRef.current = { win, fold };
        } else {
          panelPrevHRef.current = null;
        }
        setColOv(null);
        /* Item-2 fix — the standalone general-chat section is gone (chat now
           lives inside the COMMS panel), so the old `setGcOpen(false)` here is
           undefined and threw a ReferenceError on every fold click. Closing the
           COMMS overlay (setColOv(null) above) already collapses the chat. */
        setFolded(fold);
      }}
    >
      {folded ? <UnfoldVertical size={18} /> : <FoldVertical size={18} />}
    </button>
  );

  /* C56 — chrome-less INLINE panel content (no title bar, no X, no
     backdrop, no anchored fixed positioning): the block renders at the top
     of the column body and the icon that opened it toggles it closed. The
     mark-all-read link stays — it is list functionality, not chrome. */
  const inlinePanel =
    colOv &&
    (() => {
      if (colOv.kind === "matrix" && canCompactMatrix) {
        /* C62 — only the header "My" matrix remains (the per-row case
           matrix is gone; rows jump instead). */
        const mtasks = (tk || []).filter(
          (x: any) =>
            x.assignedCoId === currentUser &&
            x.status !== "completed" &&
            x.status !== "cancelled" &&
            x.status !== "archived",
        );
        return (
          <TaskMatrix
            {...({
              compact: true,
              tasks: mtasks,
              ca,
              cc, // 111 §5c — itemLinks feed for cc rows

              tkTypes,
              // BRIEF 104 §1a — `priMap` no longer passed to the matrix. It is
              // still this column's own prop: CaseTaskRow (:566) and
              // CaseEventRow (:635) both read it and both still draw levels.
              getQuadrant: userMatrixQuadrant,
              onMove: (tkId: any, q: any) => {
                if (userMatrixSetQuadrant) userMatrixSetQuadrant(tkId, q);
              },
              onTaskClick: (t2: any) => {
                closeOv();
                if (t2.caseId != null || t2.ccId != null) navTask(t2);
              },
            } as any)}
          />
        );
      }
      /* Corr-2 (item 4) — the calls panel inline, same chrome-less block as
         the matrix/notifications (item-3 narrowing makes it fit the 330px
         pop-out). Data + actions come from App via props. */
      if (colOv.kind === "calls") {
        return (
          <CallsPanel
            inline
            fill /* 209 §27 — the call log and the general chat take the room the window gives */
            co={co}
            currentUser={currentUser}
            presenceIds={presenceIds}
            presenceLoaded={presenceLoaded}
            onEnsurePresence={onPullPresence}
            calls={calls}
            busy={callBusy}
            onCall={(ids: any) => onCall && onCall(ids)}
            onNotifyMissed={onNotifyMissed}
            callActive={callActive}
            onScheduleMeeting={onScheduleMeeting}
            onSchedulePoll={onSchedulePoll}
            onSendEmail={onSendEmail}
            chatSlot={chatContent}
            onClose={closeOv}
          />
        );
      }
      return (
        <>
          {/* Corr — section title for consistency with the calls panel. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 4,
            }}
          >
            <span style={{ fontSize: FS.s13, fontWeight: 600, color: "var(--tp)", textTransform: "uppercase", letterSpacing: ".3px" }}>
              {t("notif.title", { fallback: "Notifications" })}
            </span>
            {unreadNotifCount > 0 && (
              <button
                className="colOvLink"
                onClick={() => markAllRead && markAllRead()}
              >
                {t("notif.mark_all_read", { fallback: "Mark all read" })}
              </button>
            )}
          </div>
          {notifications.length === 0 && (
            <div className="colEmpty">
              {t("notif.empty", { fallback: "No notifications" })}
            </div>
          )}
          {/* 209 §27 (d) — « make "bell" section same as in main app window »: the
              flat list of 30 is the main bell's list — NotifGroupedList under §26's
              rule, the same rows, the same row props, at the column's width. A row
              that navigates or joins pulls the main window forward and returns this
              column to its timer view. */}
          <NotifGroupedList
            notifications={notifications}
            row={{
              isNotifReadFn: isNotifRead,
              notifRead,
              tasks: tk,
              agendaEvents: ag,
              cases: ca,
              collectiveCases: cc,
              evtEntries,
              markNotifRead,
              dismissNotif,
              joinAndTime,
              onActed: () => {
                closeOv();
                try {
                  window.focus();
                } catch {
                  /* a closed main window cannot take focus */
                }
                if (!inPopOut && onToggle) onToggle();
              },
              t,
            }}
          />
        </>
      );
    })();

  return (
    <div className="CCol" ref={colRootRef}>
      {/* C33 — firm name left; the My/matrix control + the Bell (with its
          unread badge) at the far right, same panels as the app header,
          rendered inside this document. C21 — the in-app (phone) column
          keeps its back-to-app toggle (only way back there); the pop-out
          has none (browser PiP chrome owns close). */}
      <div className="CColH">
        {!inPopOut && (
          <button
            className="hamburger"
            style={{ margin: 0, flexShrink: 0 }}
            onClick={onToggle}
            title={t("col.toggle.close", { fallback: "Back to app" })}
          >
            <SquareArrowOutUpRight size={14} />
          </button>
        )}
        <span className="CColT">{firmName || t("common.app_name", { fallback: "LexCRM" })}</span>
        <span style={{ flex: 1 }} />
        {matrixBtn}
        {hourglassBtn}
        {bellBtn}
        {phoneBtn}
        {foldBtn}
      </div>
      <div className="CColB" ref={colBHint.ref}>
        {colBHint.above && <ScrollHint dir="up" />}
        {/* 31.1 (item 9) — OUTGOING waiting banner (pop-out host only):
            the Item-6 card's full lifecycle in the colCallRing idiom —
            ringing+Abort, answered+Join, declined-with-time, "No answer".
            Join opens from THIS document's window: the click grants the
            PiP window its own transient activation; a main-window open()
            from a pop-out gesture risks the popup blocker. */}
        {inPopOut &&
          outgoingCall &&
          outgoingCall.host === "popout" &&
          (() => {
            const oc = (calls || []).find(
              (c: any) => c.id === outgoingCall.callId,
            );
            if (!oc || oc.status === "canceled") return null;
            const ringing = oc.status === "ringing";
            const nameOf = (p: any) => {
              const c2 = (co || []).find((x: any) => x.id === p.collaboratorId);
              return c2
                ? `${c2.firstName || ""} ${c2.lastName || ""}`.trim() || "?"
                : "?";
            };
            const answeredP = (oc.participants || []).filter(
              (p: any) => p.outcome === "answered",
            );
            const declinedP = (oc.participants || []).filter(
              (p: any) => p.outcome === "declined",
            );
            const label = ringing
              ? t("calls.waiting", { fallback: "Waiting for an answer…" })
              : oc.status === "answered"
                ? t("calls.answered_modal", {
                    name: answeredP.length ? nameOf(answeredP[0]) : "?",
                    fallback: `${answeredP.length ? nameOf(answeredP[0]) : "?"} answered`,
                  })
                : oc.status === "missed"
                  ? t("calls.no_answer", { fallback: "No answer" })
                  : declinedP
                      .map(
                        (p: any) =>
                          `${nameOf(p)}${p.availableFrom ? ` ⏲ ${p.availableFrom}` : ""}`,
                      )
                      .join(" · ") ||
                    t("calls.outcome.declined", { fallback: "declined" });
            const color = ringing
              ? "var(--ac)"
              : oc.status === "answered"
                ? "var(--gn)"
                : oc.status === "missed"
                  ? "var(--rd)"
                  : "var(--am)";
            return (
              <div className="colCallRing" style={{ gap: 5 }}>
                <span
                  style={{
                    display: "inline-flex",
                    color,
                    animation: ringing
                      ? "p31ringPulse 1s ease-in-out infinite"
                      : "none",
                    flexShrink: 0,
                  }}
                >
                  <PhoneOutgoing size={14} />
                </span>
                <span
                  className="colCallRingName"
                  style={{ fontSize: FS.s10_5 }}
                  title={label}
                >
                  {label}
                </span>
                {ringing ? (
                  <button
                    type="button"
                    className="colCallAct dec"
                    title={t("calls.abort", { fallback: "Abort" })}
                    onClick={() => onAbortCall && onAbortCall(oc.id)}
                  >
                    <PhoneOff size={13} />
                  </button>
                ) : (
                  <>
                    {oc.status === "answered" && oc.joinUrl && (
                      <button
                        type="button"
                        className="colCallAct acc"
                        title={t("calls.join", { fallback: "Join the call" })}
                        onClick={(e) => {
                          const win =
                            (e.currentTarget.ownerDocument &&
                              e.currentTarget.ownerDocument.defaultView) ||
                            window;
                          try {
                            win.open(oc.joinUrl, "_blank");
                          } catch {
                            /* ignore */
                          }
                          onCloseOutgoing && onCloseOutgoing();
                        }}
                      >
                        <Phone size={13} />
                      </button>
                    )}
                    <button
                      type="button"
                      className="colCallAct dec"
                      style={{
                        border: "none",
                        color: "var(--tm)",
                        padding: "2px 4px",
                      }}
                      title={t("common.close", { fallback: "Close" })}
                      onClick={() => onCloseOutgoing && onCloseOutgoing()}
                    >
                      ×
                    </button>
                  </>
                )}
              </div>
            );
          })()}
        {/* Phase 31 (C2) — incoming-call banner (the pop-out's ring popup;
            sound is owned by the main window's ringer). Decline routes to
            the main window's time picker. */}
        {incomingCalls.map((ic2: any) => {
          const caller2 = (co || []).find((c: any) => c.id === ic2.initiatorId);
          return (
            <div key={ic2.callId} className="colCallRing">
              <span
                style={{
                  display: "inline-flex",
                  color: "var(--gn)",
                  animation: "p31ringPulse 1s ease-in-out infinite",
                  flexShrink: 0,
                }}
              >
                <PhoneIncoming size={16} />
              </span>
              <span className="colCallRingName">
                {caller2
                  ? `${caller2.firstName || ""} ${caller2.lastName || ""}`.trim() ||
                    caller2.name ||
                    "?"
                  : "?"}
              </span>
              <button
                type="button"
                className="colCallAct acc"
                title={t("calls.accept", { fallback: "Accept" })}
                onClick={(e) =>
                  /* 31.1 (item 9b) — pass the gesture's own window up:
                     respondCall opens its placeholder tab there, so the
                     PiP click carries its transient activation instead of
                     betting on the main window's. */
                  onCallAccept &&
                  onCallAccept(
                    ic2.callId,
                    (e.currentTarget.ownerDocument &&
                      e.currentTarget.ownerDocument.defaultView) ||
                      window,
                  )
                }
              >
                <Phone size={13} />
              </button>
              <button
                type="button"
                className="colCallAct dec"
                title={t("calls.decline", { fallback: "Decline" })}
                onClick={() => onCallDecline && onCallDecline(ic2.callId)}
              >
                <PhoneOff size={13} />
              </button>
            </div>
          );
        })}
        {/* Phase 31 (C3) — engine alert toasts (ephemeral, show-once). */}
        {alertToasts.map((a2: any) => (
          <div key={a2.uid} className="colCallRing" style={{ gap: 5 }}>
            <span
              style={{
                color:
                  a2.kind === "event_t0"
                    ? "var(--rd)"
                    : a2.kind === "event_lead"
                      ? "var(--am)"
                      : "var(--ac)",
                display: "inline-flex",
                flexShrink: 0,
              }}
            >
              <Bell size={13} />
            </span>
            <span
              className="colCallRingName"
              style={{ fontWeight: 600, fontSize: FS.s10_5 }}
              title={a2.title}
            >
              {(a2.time ? a2.time + " · " : "") + a2.title}
            </span>
            {a2.joinUrl && (
              <button
                type="button"
                className="colCallAct acc"
                title={t("alerts.join", { fallback: "Join" })}
                onClick={(e) => {
                  /* 31.1 (item 9b) — open from THIS document's window
                     (PiP transient activation, the item-9 pattern); a
                     main-window open() from a pop-out gesture risks the
                     popup blocker. No handle needed, noopener stays. */
                  const win =
                    (e.currentTarget.ownerDocument &&
                      e.currentTarget.ownerDocument.defaultView) ||
                    window;
                  win.open(a2.joinUrl, "_blank", "noopener");
                  onDismissAlert && onDismissAlert(a2.uid);
                }}
              >
                <Phone size={12} />
              </button>
            )}
            <button
              type="button"
              className="colCallAct dec"
              style={{ border: "none", color: "var(--tm)", padding: "2px 4px" }}
              title={t("common.close", { fallback: "Close" })}
              onClick={() => onDismissAlert && onDismissAlert(a2.uid)}
            >
              ×
            </button>
          </div>
        ))}
        {/* C56 — the inline matrix / notifications block leads the body;
            sections and their collapse state are untouched below it. */}
        {/* 209 §27 (c) — ONE VIEW AT A TIME: « My », the bell and the phone each
            show their own view ALONE; the hourglass view is the three sections.
            Nothing is appended under a view any more. Folded: the title bar and
            the timers, as before. */}
        {colOv && (
          <div className={"colInline" + (colOv.kind === "calls" ? " fill" : "")} ref={ovRootRef}>
            {inlinePanel}
          </div>
        )}
        {!colOv && (folded
          ? sections.filter((sec) => sec.id === "timer")
          : sections
        ).map((sec) => {
          const open = isOpen(sec.id);
          const Icon = sec.icon;
          return (
            <div className={"colSec" + (open ? " open" : "")} key={sec.id}>
              {/* C40 — every section header (chat included) is collapse-only. */}
              <div className="colSecH" onClick={() => toggleSec(sec.id)}>
                {Icon && <Icon size={13} style={{ flexShrink: 0 }} />}
                <span className="colSecT">{sec.title}</span>
                {sec.badge > 0 && (
                  <span className={"colB" + (sec.hot ? " hot" : "")}>
                    {sec.badge}
                  </span>
                )}
                <span
                  className="colSecC"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSec(sec.id);
                  }}
                >
                  {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                </span>
              </div>
              {open && <div className="colSecB">{sec.content}</div>}
            </div>
          );
        })}
        {colBHint.below && <ScrollHint dir="down" />}
      </div>
    </div>
  );
}
